import { useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { PRODUCTS } from "@/data/site";
import CheckoutModal from "@/components/CheckoutModal";

export default function ProductSection() {
  const { t, lang } = useLanguage();
  const [modalOpen, setModalOpen] = useState(false);
  const p = PRODUCTS[0];

  return (
    <section className="overflow-hidden py-24 md:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <Reveal className="relative order-2 lg:order-1">
          <div
            className="absolute -inset-10 bg-[radial-gradient(circle_at_center,rgba(212,175,55,0.16),transparent_65%)] blur-2xl"
            aria-hidden="true"
          />
          <div className="relative aspect-[4/5] overflow-hidden border hairline">
            <img
              src={p.image}
              alt={t.signature.imgAlt}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-[1.4s] ease-out hover:scale-[1.05]"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-obsidian/50 via-transparent to-transparent" />
          </div>
          <div className="absolute right-5 top-5 -translate-y-1/2 md:right-8">
            <p className="font-serif text-5xl italic text-gold/85 md:text-6xl">
              {p.currency}
              {p.price}
            </p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2">
          <Reveal>
            <Eyebrow>{t.signature.eyebrow}</Eyebrow>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-cream sm:text-5xl">
              {t.signature.heading}
            </h2>
            <p className="mt-4 text-[12px] font-medium uppercase tracking-luxe text-gold/90">
              {p.category[lang]} · {p.size}
            </p>
            <h3 className="mt-6 font-serif text-2xl italic text-gold-light">
              {p.name}
            </h3>
            <p className="mt-5 text-base leading-relaxed text-cream/70">
              {p.description[lang].split("\n")[0]}
            </p>

            <ul className="mt-7 grid gap-3 sm:grid-cols-2">
              {p.highlights.map((h, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-cream/75">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                  {h[lang]}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={`/product/${p.slug}`}
                data-testid="signature-view-details-button"
                className="bg-gold px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_36px_rgba(212,175,55,0.35)]"
              >
                {t.signature.viewDetails}
              </Link>
              <button
                onClick={() => setModalOpen(true)}
                data-testid="signature-buy-now-button"
                className="border border-gold/50 px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-gold transition-all duration-300 hover:border-gold hover:bg-gold/10"
              >
                {t.signature.buyNow}
              </button>
            </div>
          </Reveal>
        </div>
      </div>
      <CheckoutModal open={modalOpen} onClose={() => setModalOpen(false)} product={p} />
    </section>
  );
}
